import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import moment from "moment";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { formatDuration } from "../../../utils/attendance";

// Superadmin-only: correct what a day reports to the HRMS. "28 Oct went out as
// Half Day, it was a full day 09:30-18:30" -> set the times and status, and the
// corrected total is pushed to the HRMS on save. The agent's own punches are
// left untouched; the correction overlays the day.

const PAGE_SIZE = 20;
const URL = () => apiEndPointsConfig.attendanceCorrections;

const STATUS_META = {
  P: { label: "Full Day", bg: "#ecfdf5", fg: "#059669" },
  HD: { label: "Half Day", bg: "#fffbeb", fg: "#d97706" },
  A: { label: "Absent", bg: "#fef2f2", fg: "#dc2626" },
};

// Mirrors the server's default thresholds; only used to suggest a status.
const suggestStatus = (ms) => (ms >= 8 * 3600000 ? "P" : ms >= 5 * 3600000 ? "HD" : "A");

const minutesOf = (hhmm) => {
  if (!/^\d{2}:\d{2}$/.test(hhmm || "")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** out - in - break, in ms; past midnight when out is before in. */
const totalMs = (clockIn, clockOut, breakMinutes) => {
  const a = minutesOf(clockIn);
  const b = minutesOf(clockOut);
  if (a === null || b === null || a === b) return 0;
  let span = b - a;
  if (span < 0) span += 24 * 60;
  return Math.max(0, span - (Number(breakMinutes) || 0)) * 60000;
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) return <Typography variant="caption" sx={{ color: "#9ca3af" }}>—</Typography>;
  return <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700 }} />;
};

const SyncChip = ({ sync }) => {
  if (!sync) return null;
  const map = {
    sent: ["Sent", "#ecfdf5", "#059669"],
    failed: ["Failed", "#fef2f2", "#dc2626"],
    skipped: ["Skipped", "#f3f4f6", "#6b7280"],
    pending: ["Pending", "#eff6ff", "#2563eb"],
  };
  const [label, bg, fg] = map[sync.status] || map.pending;
  const tip = sync.status === "sent" && sync.syncedAt
    ? `Sent ${moment(sync.syncedAt).format("D MMM, HH:mm")}`
    : sync.lastError || "";
  return (
    <Tooltip title={tip}>
      <Chip label={label} size="small" sx={{ bgcolor: bg, color: fg, fontWeight: 600 }} />
    </Tooltip>
  );
};

const emptyForm = () => ({
  agent: null,
  date: moment().format("YYYY-MM-DD"),
  clockIn: "09:30",
  clockOut: "18:30",
  breakMinutes: 60,
  status: "P",
  reason: "",
});

export default function AttendanceCorrections() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState({ text: "", severity: "success" });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [agents, setAgents] = useState([]);
  const [filterAgent, setFilterAgent] = useState("");
  const [from, setFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [statusTouched, setStatusTouched] = useState(false);
  const [day, setDay] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(URL(), {
        params: { page, limit: PAGE_SIZE, from: from || undefined, to: to || undefined, agentId: filterAgent || undefined },
      });
      setRows(data?.data || []);
      setPages(data?.pagination?.pages || 1);
      setTotal(data?.pagination?.total || 0);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load corrections");
    } finally {
      setLoading(false);
    }
  }, [page, from, to, filterAgent]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [from, to, filterAgent]);

  useEffect(() => {
    axiosInstance
      .get(apiEndPointsConfig.agents, { params: { brief: true } })
      .then(({ data }) => setAgents(data?.data || []))
      .catch(() => setAgents([]));
  }, []);

  // Whenever agent + date are both set, show what that day currently reports
  // and pre-fill from an existing correction.
  useEffect(() => {
    if (!open || !form.agent || !form.date) {
      setDay(null);
      return;
    }
    let cancelled = false;
    setDayLoading(true);
    axiosInstance
      .get(`${URL()}/day`, { params: { agentId: form.agent.id, date: form.date } })
      .then(({ data }) => {
        if (cancelled) return;
        const d = data?.data || null;
        setDay(d);
        const c = d?.correction;
        if (c) {
          setForm((f) => ({
            ...f,
            clockIn: c.clockIn,
            clockOut: c.clockOut,
            breakMinutes: c.breakMinutes,
            status: c.status,
            reason: "",
          }));
          setStatusTouched(true);
        } else if (d?.actual?.firstIn && d?.actual?.lastOut) {
          setForm((f) => ({ ...f, clockIn: d.actual.firstIn, clockOut: d.actual.lastOut }));
        }
      })
      .catch((err) => !cancelled && setFormError(err?.response?.data?.message || "Could not load that day"))
      .finally(() => !cancelled && setDayLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, form.agent, form.date]);

  const computed = useMemo(
    () => totalMs(form.clockIn, form.clockOut, form.breakMinutes),
    [form.clockIn, form.clockOut, form.breakMinutes]
  );
  const suggested = suggestStatus(computed);

  // Keep the status following the times until the superadmin picks one.
  useEffect(() => {
    if (!statusTouched) setForm((f) => ({ ...f, status: suggested }));
  }, [suggested, statusTouched]);

  const openNew = () => {
    setForm(emptyForm());
    setStatusTouched(false);
    setDay(null);
    setFormError("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      agent: agents.find((a) => a.id === row.agentId) || { id: row.agentId, name: row.agentName, userid: row.agentUserId },
      date: row.date,
      clockIn: row.clockIn,
      clockOut: row.clockOut,
      breakMinutes: row.breakMinutes,
      status: row.status,
      reason: "",
    });
    setStatusTouched(true);
    setFormError("");
    setOpen(true);
  };

  const save = async () => {
    setFormError("");
    if (!form.agent) return setFormError("Pick an agent");
    if (!computed) return setFormError("Clock in and clock out must be set and different");
    if (form.reason.trim().length < 5) return setFormError("Give a reason for the correction");

    setSaving(true);
    try {
      const { data } = await axiosInstance.post(URL(), {
        agentId: form.agent.id,
        date: form.date,
        clockIn: form.clockIn,
        clockOut: form.clockOut,
        breakMinutes: Number(form.breakMinutes) || 0,
        status: form.status,
        reason: form.reason.trim(),
      });
      setNotice({ text: data?.message || "Saved", severity: data?.hrmsSent ? "success" : "warning" });
      setOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Could not save the correction");
    } finally {
      setSaving(false);
    }
  };

  const resend = async (row) => {
    try {
      const { data } = await axiosInstance.post(`${URL()}/${row.id}/resend`);
      setNotice({ text: data?.message || "Sent to HRMS", severity: "success" });
    } catch (err) {
      setNotice({ text: err?.response?.data?.message || "HRMS sync failed", severity: "error" });
    }
    load();
  };

  const confirmRemove = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.delete(`${URL()}/${removing.id}`);
      setNotice({ text: data?.message || "Correction removed", severity: "success" });
      setRemoving(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the correction");
    } finally {
      setSaving(false);
    }
  };

  const today = moment().format("YYYY-MM-DD");

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice.text && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice({ text: "", severity: "success" })}>
          {notice.text}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField
          select
          label="Agent"
          size="small"
          value={filterAgent}
          onChange={(e) => setFilterAgent(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All agents</MenuItem>
          {agents.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name} ({a.userid})
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openNew}
          sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px", textTransform: "none" }}
        >
          New Correction
        </Button>
      </Paper>

      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Before</TableCell>
                  <TableCell>Corrected</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>HRMS</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                      No corrections in this range.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{moment(r.date).format("D MMM YYYY")}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.agentName}</Typography>
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>{r.agentUserId}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.original?.status} />
                      <Typography variant="caption" sx={{ display: "block", color: "#6b7280" }}>
                        {formatDuration(r.original?.productiveMs || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.status} />
                      <Typography variant="caption" sx={{ display: "block", color: "#374151" }}>
                        {r.clockIn} – {r.clockOut}
                        {r.breakMinutes ? ` · ${r.breakMinutes}m break` : ""} · <strong>{formatDuration(r.productiveMs)}</strong>
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Tooltip title={r.reason}>
                        <Typography variant="body2" noWrap>{r.reason}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.updatedByName || r.createdByName}</Typography>
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                        {moment(r.updatedAt).format("D MMM, HH:mm")}
                      </Typography>
                    </TableCell>
                    <TableCell><SyncChip sync={r.hrmsSync} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Resend to HRMS">
                        <IconButton size="small" onClick={() => resend(r)}><RefreshCw size={16} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}><Pencil size={16} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Remove correction">
                        <IconButton size="small" onClick={() => setRemoving(r)} sx={{ color: "#dc2626" }}><Trash2 size={16} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {total > 0 && pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Pagination count={pages} page={page} onChange={(e, v) => setPage(v)} size="small" shape="rounded" />
        </Box>
      )}

      {/* ── Correction form ─────────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Attendance Correction</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Autocomplete
              options={agents}
              value={form.agent}
              onChange={(e, v) => setForm((f) => ({ ...f, agent: v }))}
              getOptionLabel={(a) => (a ? `${a.name} (${a.userid})` : "")}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              sx={{ flex: 2, minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Agent" size="small" />}
            />
            <TextField
              label="Date"
              type="date"
              size="small"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              inputProps={{ max: today }}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 160 }}
            />
          </Box>

          {form.agent && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", bgcolor: "#f9fafb", border: "1px solid #e5e7eb" }}>
              {dayLoading ? (
                <CircularProgress size={18} />
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700 }}>
                    CURRENTLY RECORDED
                  </Typography>
                  {day?.actual ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                      <StatusChip status={day.actual.status} />
                      <Typography variant="body2">
                        {formatDuration(day.actual.productiveMs)} productive · {day.actual.firstIn} – {day.actual.lastOut}
                        {day.actual.shifts > 1 ? ` · ${day.actual.shifts} shifts` : ""}
                        {day.actual.lateCut ? " · cut to HD for 3rd late" : ""}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>No punches recorded — the day counts as Absent.</Typography>
                  )}
                  {day?.correction && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#d97706" }}>
                      Already corrected by {day.correction.updatedByName} to {STATUS_META[day.correction.status]?.label} ({formatDuration(day.correction.productiveMs)}). Saving replaces it.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
            <TextField
              label="Clock In"
              type="time"
              size="small"
              value={form.clockIn}
              onChange={(e) => setForm((f) => ({ ...f, clockIn: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              label="Clock Out"
              type="time"
              size="small"
              value={form.clockOut}
              onChange={(e) => setForm((f) => ({ ...f, clockOut: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              label="Break (min)"
              type="number"
              size="small"
              value={form.breakMinutes}
              onChange={(e) => setForm((f) => ({ ...f, breakMinutes: e.target.value }))}
              inputProps={{ min: 0, step: 5 }}
              sx={{ flex: 1, minWidth: 110 }}
            />
          </Box>

          <Box sx={{ mt: 1.5, display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>Total productive time sent to HRMS:</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#0C237B" }}>{formatDuration(computed)}</Typography>
            {minutesOf(form.clockOut) !== null && minutesOf(form.clockOut) < minutesOf(form.clockIn) && (
              <Typography variant="caption" sx={{ color: "#6b7280" }}>(ends next day)</Typography>
            )}
          </Box>

          <Typography variant="body2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>Attendance status</Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={form.status}
            onChange={(e, v) => {
              if (!v) return;
              setStatusTouched(true);
              setForm((f) => ({ ...f, status: v }));
            }}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600 } }}
          >
            {Object.entries(STATUS_META).map(([key, m]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{ "&.Mui-selected": { bgcolor: `${m.bg} !important`, color: `${m.fg} !important` } }}
              >
                {m.label} ({key})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {form.status !== suggested && (
            <Typography variant="caption" sx={{ color: "#d97706" }}>
              By the hours this would be {STATUS_META[suggested].label}; you have chosen {STATUS_META[form.status].label}.
            </Typography>
          )}

          <TextField
            label="Reason"
            placeholder="e.g. Biometric failure — agent was on the floor 09:30 to 18:30, confirmed by TL"
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px", textTransform: "none" }}
          >
            {saving ? "Saving & sending..." : "Save & Send to HRMS"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove correction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{removing?.agentName}</strong>'s {removing && moment(removing.date).format("D MMM")} will go back to
            what they actually recorded, and that will be re-sent to the HRMS.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoving(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={confirmRemove} sx={{ borderRadius: "8px" }}>
            {saving ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
